/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Footer as FooterDI, type CustomItemComponentProps } from '@atlaskit/side-navigation';
import { useSidebarTheme } from '../../controllers/sidebar-theme/index.tsx';
import type { ProjectFooterProps } from './types.tsx';

const bgColorCssVar = '--project-footer-bg-color';
const textColorCssVar = '--project-footer-text-color';

const customFooterStyles = css({
	// Need to increase specificity here until CustomItem has been migrated to @compiled/react
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		padding: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	div: {
		display: 'block',
		textAlign: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-item-title]': {
		whiteSpace: 'normal',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 'initial',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-item-description] > button': {
		color: token('color.text.subtle'),
		marginTop: token('space.075'),
		marginRight: token('space.075'),
		marginBottom: token('space.075'),
		marginLeft: token('space.075'),
		display: 'inline-block',
		'&:hover': {
			color: token('color.text.subtle'),
		},
	},
});

const bgColorThemeStyles = css({
	// Need to increase specificity here until CustomItem has been migrated to @compiled/react
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		backgroundColor: `var(${bgColorCssVar})`,
	},
});

const colorThemeStyles = css({
	// Need to increase specificity here until CustomItem has been migrated to @compiled/react
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&, [data-item-title], [data-item-description]': {
		color: `var(${textColorCssVar})`,
	},
});

export const CustomFooter = ({ className, ...props }: CustomItemComponentProps) => {
	const theme = useSidebarTheme();
	const item = theme?.navigation?.item;
	return (
		<div
			{...props}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			css={[
				customFooterStyles,
				item?.background?.default && bgColorThemeStyles,
				item?.color?.default && colorThemeStyles,
			]}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				{
					[bgColorCssVar]: item?.background?.default,
					[textColorCssVar]: item?.color?.default,
				} as React.CSSProperties
			}
		/>
	);
};

export const ProjectFooter = ({ Footer = FooterDI, children, description }: ProjectFooterProps) => {
	return (
		<Footer
			component={CustomFooter}
			// @ts-expect-error - TS2769 - No overload matches this call.
			description={description}
			testId="navigation-apps-sidebar-common.ui.project-footer.test-footer"
		>
			{children}
		</Footer>
	);
};
